import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL, fetchWithAuth, getImageUrl } from '../api';
import { format, parseISO, isPast } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  Plus, Users, Calendar, MapPin, Clock, UserCheck, 
  Search, Filter, MoreVertical, Trash2, Slash, Activity, 
  DollarSign, Layers, Grid, List, CheckCircle, XCircle
} from 'react-feather';

// KPI Card Component
const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-[#ffffff03] border border-white/5 rounded-2xl p-6 flex items-start justify-between relative overflow-hidden group">
    <div className={`absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity ${color}`}>
        <Icon size={64} />
    </div>
    <div>
      <p className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-1">{title}</p>
      <h3 className="text-3xl font-bold text-white">{value}</h3>
    </div>
    <div className={`p-3 rounded-xl bg-white/5 ${color} text-white`}>
      <Icon size={24} />
    </div>
  </div>
);

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Data States
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // UI States
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard | events | users
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // User Filters
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [actionMenuOpen, setActionMenuOpen] = useState(null); // ID del usuario con menú abierto

  // Modals States
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showDuplicateEvent, setShowDuplicateEvent] = useState(false);
  
  // Selection States
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [duplicateData, setDuplicateData] = useState({ date: '', time: '' });
  const [transferData, setTransferData] = useState({ new_owner_username: '' });
  
  // New Event Form State
  const [newEvent, setNewEvent] = useState({
    name: '', date: '', time: '', description: '', image_url: '',
    location: '', direction: '', age_limit: 18, creator_username: '', private: false
  });

  // --- INITIAL DATA FETCH ---
  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventsRes, usersRes] = await Promise.all([
        fetchWithAuth(`${API_URL}/all-events`),
        fetchWithAuth(`${API_URL}/admin/users`)
      ]);
      setEvents(eventsRes);
      setUsers(usersRes);
    } catch (err) { setError('Error al cargar datos del sistema'); } 
    finally { setLoading(false); }
  };

  // --- EVENT ACTIONS ---
  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      await fetchWithAuth(`${API_URL}/admin/events`, { method: 'POST', body: JSON.stringify(newEvent) });
      showSuccess('Evento creado exitosamente');
      setShowCreateEvent(false);
      // Reset form
      setNewEvent({ name: '', date: '', time: '', description: '', image_url: '', location: '', direction: '', age_limit: 18, creator_username: '', private: false });
      fetchData();
    } catch (err) { setError(err.message); }
  };

  const handleDuplicateEvent = async (e) => {
    e.preventDefault();
    try {
      await fetchWithAuth(`${API_URL}/events/${selectedEvent.id}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_date: `${duplicateData.date}T${duplicateData.time}` }),
      });
      showSuccess('Evento duplicado correctamente');
      setShowDuplicateEvent(false);
      setDuplicateData({ date: '', time: '' });
      fetchData();
    } catch (err) { setError('Error duplicando evento'); }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("¿Eliminar este evento permanentemente?")) return;
    try {
      await fetchWithAuth(`${API_URL}/admin/events/${eventId}`, { method: "DELETE" });
      setEvents(prev => prev.filter(e => e.id !== eventId));
      showSuccess('Evento eliminado');
    } catch (err) { setError("No se pudo eliminar el evento."); }
  };

  const handleTransferOwnership = async (e) => {
    e.preventDefault();
    try {
      await fetchWithAuth(`${API_URL}/admin/events/${selectedEvent.id}/transfer-ownership`, {
        method: 'PATCH',
        body: JSON.stringify(transferData)
      });
      showSuccess('Propiedad transferida');
      setShowTransferModal(false);
      fetchData();
    } catch (err) { setError(err.message); }
  };

  // --- USER ACTIONS ---
  const handleRoleChange = async (userId, newRole) => {
    try {
      await fetchWithAuth(`${API_URL}/admin/users/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole })
      });
      // Optimistic update
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      showSuccess(`Rol actualizado a ${newRole}`);
    } catch (err) { setError(err.message); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) return;
    try {
      await fetchWithAuth(`${API_URL}/admin/users/${userId}`, { method: 'DELETE' });
      setUsers(prev => prev.filter(u => u.id !== userId));
      showSuccess('Usuario eliminado');
    } catch (err) { setError(err.message); }
  };

  // --- HELPERS ---
  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };
  
  // Filter Users Logic
  const filteredUsers = users.filter(u => {
    const searchTerm = userSearch.toLowerCase();
    
    // Usamos (u.campo || '') para que si es null, use un string vacío y no rompa la app
    const nameMatch = (u.name || '').toLowerCase().includes(searchTerm);
    const emailMatch = (u.email || '').toLowerCase().includes(searchTerm);
    const usernameMatch = (u.username || '').toLowerCase().includes(searchTerm);

    const matchesSearch = nameMatch || emailMatch || usernameMatch;
    
    const matchesRole = userRoleFilter === 'all' || u.role === userRoleFilter;
    
    return matchesSearch && matchesRole;
  });

  // Close Action Menu on Click Outside
  useEffect(() => {
    const closeMenu = () => setActionMenuOpen(null);
    if(actionMenuOpen) document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, [actionMenuOpen]);

  if (loading) return <div className="min-h-screen bg-[#020205] text-white flex items-center justify-center">Cargando Admin...</div>;

  return (
    <div className="min-h-screen bg-[#020205] text-white pb-20 font-sans">
      
      {/* Top Background Gradient */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-purple-900/20 to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 pt-24 relative z-10">
        
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Admin Dashboard</h1>
            <div className="flex items-center gap-4 text-gray-400 text-sm">
                <span>Hola, {user.name}</span>
                <span className="w-1 h-1 rounded-full bg-gray-600"/>
                <span>{format(new Date(), "d 'de' MMMM, yyyy", { locale: es })}</span>
            </div>
          </div>
          
          <button
            onClick={() => setShowCreateEvent(true)}
            className="bg-white text-black px-6 py-3 rounded-xl font-bold hover:scale-105 transition-all flex items-center gap-2 shadow-lg shadow-white/10"
          >
            <Plus size={20} /> Crear Evento
          </button>
        </div>

        {/* ALERTS */}
        {error && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-center">{error}</div>}
        {success && <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-xl text-green-400 text-center">{success}</div>}

        {/* TABS NAVIGATION */}
        <div className="flex items-center gap-1 bg-[#ffffff05] p-1 rounded-xl w-fit mb-8 border border-white/5">
            {[
                { id: 'dashboard', label: 'Indicadores', icon: Activity },
                { id: 'events', label: 'Eventos', icon: Calendar },
                { id: 'users', label: 'Usuarios', icon: Users },
            ].map(tab => (
                <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        activeTab === tab.id ? 'bg-white text-black shadow-md' : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                >
                    <tab.icon size={16} /> {tab.label}
                </button>
            ))}
        </div>

        {/* --- TAB: DASHBOARD (INDICADORES) --- */}
        {activeTab === 'dashboard' && (
            <div className="space-y-8 animate-fade-in">
                {/* KPI Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard title="Usuarios Totales" value={users.length} icon={Users} color="text-blue-400" />
                    <StatCard title="Eventos Creados" value={events.length} icon={Calendar} color="text-purple-400" />
                    {/* Placeholder Logic for QR: Users * 1.5 (Simulated) */}
                    <StatCard title="QRs Generados" value={Math.floor(users.length * 1.5)} icon={Layers} color="text-green-400" />
                    <StatCard title="Revenue Total" value="S/. 0.00" icon={DollarSign} color="text-yellow-400" />
                </div>
            </div>
        )}

        {/* --- TAB: EVENTOS --- */}
        {activeTab === 'events' && (
            <div className="space-y-6 animate-fade-in">
            <div className="grid gap-4">
                {events.map(event => {
                    const isFinished = isPast(parseISO(`${event.date}T${event.time || '00:00'}`));
                    
                    return (
                        <div key={event.id} className="bg-[#ffffff03] border border-white/5 rounded-2xl p-5 hover:border-white/10 transition-all flex flex-col md:flex-row gap-6 group">
                            
                            {/* Image (Clickable con navigate) */}
                            <div 
                                onClick={() => navigate(`/event/${event.id}`)}
                                className="w-full md:w-48 h-32 rounded-xl bg-gray-800 overflow-hidden shrink-0 relative cursor-pointer hover:opacity-80 transition-opacity"
                            >
                                <img src={getImageUrl(event.image_url)} className="w-full h-full object-cover" alt="" />
                                <div className="absolute inset-0 bg-black/20"/>
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-start">
                                    <div>
                                        {/* Título (Clickable con navigate) */}
                                        <h3 
                                            onClick={() => navigate(`/event/${event.id}`)}
                                            className="text-xl font-bold text-white mb-1 cursor-pointer hover:text-indigo-400 transition-colors"
                                        >
                                            {event.name}
                                        </h3>

                                        <div className="flex items-center gap-2 mb-3">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                                                isFinished ? 'bg-gray-500/20 text-gray-400 border-gray-500/30' : 'bg-green-500/20 text-green-400 border-green-500/30'
                                            }`}>
                                                {isFinished ? 'Finalizado' : 'Activo'}
                                            </span>
                                            <span className="text-gray-500 text-sm flex items-center gap-1">
                                                <UserCheck size={12}/> {event.creator?.name || 'Admin'}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* Quick Actions (Evitamos que el click burbujee hacia navigate si fuera necesario, pero aquí son botones independientes) */}
                                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={(e) => { e.stopPropagation(); setSelectedEvent(event); setShowDuplicateEvent(true); }} className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-orange-400" title="Duplicar">
                                            <Calendar size={18}/>
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); setSelectedEvent(event); setShowTransferModal(true); }} className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-blue-400" title="Transferir">
                                            <Users size={18}/>
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event.id); }} className="p-2 bg-white/5 hover:bg-red-500/20 rounded-lg text-red-400" title="Eliminar">
                                            <Trash2 size={18}/>
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-400 mt-2">
                                    <div className="flex items-center gap-2"><Calendar size={14}/> {format(parseISO(event.date), "d MMM yyyy", { locale: es })}</div>
                                    <div className="flex items-center gap-2"><Clock size={14}/> {event.time}</div>
                                    <div className="flex items-center gap-2"><MapPin size={14}/> {event.location}</div>
                                    <div className="flex items-center gap-2"><Users size={14}/> +{event.age_limit}</div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            </div>
        )}

        {/* --- TAB: USUARIOS (TABLA) --- */}
        {activeTab === 'users' && (
            <div className="animate-fade-in space-y-6">
                {/* Filters */}
                <div className="flex flex-col md:flex-row gap-4 bg-[#ffffff03] p-4 rounded-xl border border-white/5">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                        <input 
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                            placeholder="Buscar por nombre, email o username..."
                            className="w-full bg-black/50 border border-white/10 rounded-lg pl-10 pr-4 py-2 text-white focus:border-purple-500 outline-none"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Filter className="text-gray-500" size={18} />
                        <select 
                            value={userRoleFilter}
                            onChange={(e) => setUserRoleFilter(e.target.value)}
                            className="bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white outline-none focus:border-purple-500"
                        >
                            <option value="all">Todos los roles</option>
                            <option value="admin">Admin</option>
                            <option value="user">User</option>
                        </select>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto rounded-xl border border-white/5">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-[#ffffff05] text-gray-400 uppercase font-medium">
                            <tr>
                                <th className="px-6 py-4">Usuario</th>
                                <th className="px-6 py-4">Contacto</th>
                                <th className="px-6 py-4">Rol</th>
                                <th className="px-6 py-4 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {filteredUsers.map(u => (
                                <tr key={u.id} className="hover:bg-[#ffffff02] transition-colors group">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-700 to-black flex items-center justify-center font-bold text-white border border-white/10">
                                                {u.name.charAt(0)}
                                            </div>
                                            <div>
                                                <div className="font-medium text-white">{u.name}</div>
                                                <div className="text-gray-500 text-xs">ID: {u.id}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-gray-300">{u.email}</div>
                                        <div className="text-gray-500 text-xs">@{u.username}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <select
                                            value={u.role}
                                            onChange={(e) => handleRoleChange(u.id, e.target.value)}
                                            className={`
                                                bg-transparent border border-white/10 rounded px-2 py-1 text-xs font-medium uppercase outline-none cursor-pointer hover:border-white/30
                                                ${u.role === 'admin' ? 'text-purple-400' : 'text-blue-400'}
                                            `}
                                        >
                                            <option value="user" className="bg-black text-gray-300">User</option>
                                            <option value="admin" className="bg-black text-purple-400">Admin</option>
                                        </select>
                                    </td>
                                    <td className="px-6 py-4 text-right relative">
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); setActionMenuOpen(actionMenuOpen === u.id ? null : u.id); }}
                                            className="p-2 text-gray-500 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
                                        >
                                            <MoreVertical size={18} />
                                        </button>
                                        
                                        {/* Dropdown Menu */}
                                        {actionMenuOpen === u.id && (
                                            <div className="absolute right-8 top-8 w-40 bg-[#1A1A20] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in">
                                                <button onClick={() => {}} className="w-full text-left px-4 py-2.5 hover:bg-white/5 text-gray-300 flex items-center gap-2">
                                                    <Slash size={14}/> Banear
                                                </button>
                                                <button onClick={() => handleDeleteUser(u.id)} className="w-full text-left px-4 py-2.5 hover:bg-red-500/10 text-red-400 flex items-center gap-2">
                                                    <Trash2 size={14}/> Eliminar
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredUsers.length === 0 && (
                        <div className="p-8 text-center text-gray-500">No se encontraron usuarios.</div>
                    )}
                </div>
            </div>
        )}

      </div>

      {/* --- MODALS (Dark Theme) --- */}
      
      {/* Create Event Modal */}
      {showCreateEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
             <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">Nuevo Evento</h2>
                <button onClick={() => setShowCreateEvent(false)}><XCircle className="text-gray-500 hover:text-white"/></button>
             </div>
             <form onSubmit={handleCreateEvent} className="space-y-4">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input placeholder="Nombre" value={newEvent.name} onChange={e => setNewEvent({...newEvent, name: e.target.value})} className="bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white w-full" required/>
                    <select value={newEvent.creator_username} onChange={e => setNewEvent({...newEvent, creator_username: e.target.value})} className="bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white w-full" required>
                        <option value="">Creador (Dueño)</option>
                        {users.map(u => <option key={u.id} value={u.username}>{u.name}</option>)}
                    </select>
                    <input type="date" value={newEvent.date} onChange={e => setNewEvent({...newEvent, date: e.target.value})} className="bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white w-full" required/>
                    <input type="time" value={newEvent.time} onChange={e => setNewEvent({...newEvent, time: e.target.value})} className="bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white w-full" required/>
                    <input placeholder="Ubicación" value={newEvent.location} onChange={e => setNewEvent({...newEvent, location: e.target.value})} className="bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white w-full" required/>
                    <input placeholder="Dirección" value={newEvent.direction} onChange={e => setNewEvent({...newEvent, direction: e.target.value})} className="bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white w-full" required/>
                    <input placeholder="URL Imagen" value={newEvent.image_url} onChange={e => setNewEvent({...newEvent, image_url: e.target.value})} className="bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white w-full" required/>
                    <input type="number" placeholder="Edad Min" value={newEvent.age_limit} onChange={e => setNewEvent({...newEvent, age_limit: e.target.value})} className="bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white w-full" required/>
                 </div>
                 <textarea placeholder="Descripción" value={newEvent.description} onChange={e => setNewEvent({...newEvent, description: e.target.value})} className="bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white w-full h-24" required/>
                 <div className="flex justify-end gap-3 pt-4">
                    <button type="button" onClick={() => setShowCreateEvent(false)} className="px-4 py-2 text-gray-400 hover:text-white">Cancelar</button>
                    <button type="submit" className="px-6 py-2 bg-purple-600 rounded-lg text-white font-bold hover:bg-purple-700">Crear</button>
                 </div>
             </form>
          </div>
        </div>
      )}

      {/* Duplicate Event Modal */}
      {showDuplicateEvent && (
         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="bg-[#121212] border border-white/10 rounded-2xl p-6 max-w-sm w-full">
               <h3 className="text-lg font-bold text-white mb-4">Añadir Fecha (Duplicar)</h3>
               <form onSubmit={handleDuplicateEvent} className="space-y-4">
                  <input type="date" value={duplicateData.date} onChange={e => setDuplicateData({...duplicateData, date: e.target.value})} className="bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white w-full" required/>
                  <input type="time" value={duplicateData.time} onChange={e => setDuplicateData({...duplicateData, time: e.target.value})} className="bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white w-full" required/>
                  <div className="flex justify-end gap-3 pt-2">
                     <button type="button" onClick={() => setShowDuplicateEvent(false)} className="text-gray-400 hover:text-white">Cancelar</button>
                     <button type="submit" className="px-4 py-2 bg-orange-600 text-white rounded-lg font-bold">Duplicar</button>
                  </div>
               </form>
            </div>
         </div>
      )}

       {/* Transfer Modal */}
       {showTransferModal && (
         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="bg-[#121212] border border-white/10 rounded-2xl p-6 max-w-sm w-full">
               <h3 className="text-lg font-bold text-white mb-4">Transferir Propiedad</h3>
               <p className="text-gray-400 text-sm mb-4">Elige al nuevo dueño de "{selectedEvent?.name}":</p>
               <form onSubmit={handleTransferOwnership} className="space-y-4">
                  <select value={transferData.new_owner_username} onChange={e => setTransferData({...transferData, new_owner_username: e.target.value})} className="bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white w-full" required>
                     <option value="">Seleccionar Usuario</option>
                     {users.map(u => <option key={u.id} value={u.username}>{u.name}</option>)}
                  </select>
                  <div className="flex justify-end gap-3 pt-2">
                     <button type="button" onClick={() => setShowTransferModal(false)} className="text-gray-400 hover:text-white">Cancelar</button>
                     <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg font-bold">Transferir</button>
                  </div>
               </form>
            </div>
         </div>
      )}

    </div>
  );
}